import React, { useEffect, useState } from 'react';
import {
  Paper, Box, MenuItem, FormControl, Select, InputLabel,
  Typography, Stack, Divider, TextField,
  IconButton
} from '@mui/material';
import ActionButtons from '../../../commun/ActionButtons';
import EditableField from './EditableField';
import './detailTalent.css';
import {
  languageLevelOptions,
  languageOptions
} from '../../../commun/constants/options';
import ChipInput from '../../../commun/ChipInput';
import { fetchSkillsFromLightcast } from '../../../../services/lightcastApi';
import DeleteIcon from '@mui/icons-material/Delete';

const displayOrUnknown = (value) => {
  if (!value) return 'Non renseigné';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Non renseigné';
  return value.toString().trim() !== '' ? value : 'Non renseigné';
};

const ProfilRecherche = ({
  editableTalent,
  originalTalent,
  setEditableTalent,
  onSave,
  onFieldChange
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [skillSuggestions, setSkillSuggestions] = useState([]);
  const s = (v) => (typeof v === 'string' ? v : v == null ? '' : String(v));

  const handleEdit = () => setIsEditing(true);
  const handleCancel = () => {
    setEditableTalent(originalTalent);
    setIsEditing(false);
  };
  const handleSave = async () => {
    await onSave();
    setIsEditing(false);
  };

  const isTalent = editableTalent?.origine !== 'Candidat';

  const competencesList = Array.isArray(editableTalent?.competences)
      ? editableTalent.competences
      : typeof editableTalent?.competences === 'string'
        ? editableTalent.competences.split(',').map((item) => item.trim())
        : []


  let languesList = [];
  if (Array.isArray(editableTalent?.langues)) {
    languesList = editableTalent.langues;
  } else if (typeof editableTalent?.langues === 'string') {
    try {
      languesList = JSON.parse(editableTalent.langues);
    } catch (error) {
      console.error('Error parsing langues JSON:', error);
    }
  }

  const handleLangueChange = (index, field, value) => {
    const updatedLangues = [...(editableTalent.langues || [])];
    updatedLangues[index] = {
      ...updatedLangues[index],
      [field]: value
    };
    setEditableTalent({ ...editableTalent, langues: updatedLangues });
  };

  const ajouterLangue = () => {
    const updated = [...(editableTalent.langues || [])];
    updated.push({ langue: "", niveau: "" });
    setEditableTalent({ ...editableTalent, langues: updated });
  };

    let certificatsList = [];
    if (Array.isArray(editableTalent?.certificats)) {
      certificatsList = editableTalent.certificats;
    } else if (typeof editableTalent?.certificats === 'string') {
      try {
        certificatsList = JSON.parse(editableTalent.certificats);
      } catch (error) {
        console.error('Error parsing certificats JSON:', error);
      }
    }
  
    const handleCertificatChange = (index, field, value) => {
      const updated = [...(editableTalent.certificats || [])];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      setEditableTalent({ ...editableTalent, certificats: updated });
    };

    const ajouterCertificat = () => {
      const updated = [...(editableTalent.certificats || [])];
      updated.push({ title: "", date: "" });
      setEditableTalent({ ...editableTalent, certificats: updated });
    };

    const supprimerCertificat = (indexToRemove) => {
      const updated = (editableTalent.certificats || []).filter((_, idx) => idx !== indexToRemove);
      setEditableTalent({ ...editableTalent, certificats: updated });
    };
    
  return (
    <Paper elevation={3} className="mission-card-modern">
      <Box className="mission-header">
        <Box className="mission-header-left">
          <Typography variant="h6" className="mission-title">
            Formation et Compétences
          </Typography>
        </Box>
        <Box className="mission-header-right">
          <ActionButtons
            isEditing={isEditing}
            onEdit={handleEdit}
            onCancel={handleCancel}
            onSave={handleSave}
            showView={false}
            showDelete={false}
            showDownload={false}
          />
        </Box>
      </Box>

      <Divider className="mission-divider" />

      {/* Ligne 1 : Niveau d'études / Diplôme */}
      <Box display="flex" gap={2} mb={2}>
        <Box flex={1}>
          <EditableField
            label="Niveau d'études"
            field="niveauEtudes"
            isEditing={isEditing}
            value={s(editableTalent?.niveauEtudes).replace(/"/g, '')}
            onChange={onFieldChange}
          />
        </Box>
        <Box flex={1}>
          <EditableField
            label="Diplôme"
            field={isTalent ? 'titre' : 'profil'}
            isEditing={isEditing}
            value={s(editableTalent?.titre ?? editableTalent?.profil).replace(/"/g, '')}
            onChange={onFieldChange}
          />
        </Box>
      </Box>

      {/* Ligne 2 : Date / Établissement */}
      <Box display="flex" gap={2} mb={2}>
        <Box flex={1}>
          <EditableField
            label="Date"
            field="date"
            isEditing={isEditing}
            value={s(editableTalent?.date).replace(/"/g, '')}
            onChange={onFieldChange}
          />
        </Box>
        <Box flex={1}>
          <EditableField
            label="Établissement"
            field="etablissement"
            isEditing={isEditing}
            value={s(editableTalent?.etablissement).replace(/"/g, '')}
            onChange={onFieldChange}
          />
        </Box>
      </Box>

      {/* Ligne Langues */}
      <Typography variant="subtitle2" className="label-text">Langues</Typography>
      {languesList.length === 0 && !isEditing && (
        <Typography>Non renseigné</Typography>
      )}
      {languesList.map((lang, i) => (
        <Box key={i} display="flex" gap={2} mt={1}>
          {isEditing ? (
            <>
              <FormControl fullWidth>
                <InputLabel>Langue</InputLabel>
                <Select
                  value={lang.langue || ''}
                  onChange={(e) => handleLangueChange(i, 'langue', e.target.value)}
                >
                  <MenuItem value="">-- Langue --</MenuItem>
                  {languageOptions.map((opt, idx) => (
                    <MenuItem key={idx} value={opt.label}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Niveau</InputLabel>
                <Select
                  value={lang.niveau || ''}
                  onChange={(e) => handleLangueChange(i, 'niveau', e.target.value)}
                >
                  <MenuItem value="">-- Niveau --</MenuItem>
                  {languageLevelOptions.map((opt, idx) => (
                    <MenuItem key={idx} value={opt.label}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : (
            <>
              <Typography>{lang.langue || 'Non renseigné'}</Typography>
              <Typography>{lang.niveau || 'Non renseigné'}</Typography>
            </>
          )}
        </Box>
      ))}
      {isEditing && (
        <Box mt={1} mb={2}>
          <button className="btn-outline" onClick={ajouterLangue}>+ Ajouter une langue</button>
        </Box>
      )}

      {/* Ligne : Expérience + Compétences */}
      <Box display="flex" gap={2} mt={3}>
        <Box flex={1}>
          <EditableField
            label="Expérience du talent"
            field={isTalent ? 'experienceTalent' : 'synthese'}
            isEditing={isEditing}
            value={s(editableTalent?.experienceTalent ?? editableTalent?.synthese).replace(/"/g, '')}
            onChange={onFieldChange}
          />
        </Box>
        <Box flex={1}>
          <Typography variant="subtitle2" className="label-text">
            Compétences
          </Typography>
          {isEditing ? (
            <ChipInput
              values={competencesList}
              onChange={(newCompetences) =>
                onFieldChange('competences', newCompetences)
              }
              suggestions={skillSuggestions}
              onInputChange={async (text) => {
                if (text.length >= 2) {
                  try {
                    const result = await fetchSkillsFromLightcast(text);
                    setSkillSuggestions(result);
                  } catch (error) {
                    console.error('Erreur Lightcast:', error);
                  }
                }
              }}
              fullWidth
            />
          ) : (
            <Stack spacing={0.5} direction="row" flexWrap="wrap">
              {competencesList.length > 0 ? (
                competencesList.map((competence, i) => (
                  <Typography key={i} variant="body2" sx={{ marginRight: 1 }}>
                    {competence}
                  </Typography>
                ))
              ) : (
                <Typography>Non renseigné</Typography>
              )}
            </Stack>
          )}
        </Box>
      </Box>

        {/* Ligne Certificats */}
        <Typography variant="subtitle2" className="label-text">Certificats</Typography>

        {(certificatsList.length === 0 && !isEditing) && (
          <Typography>Non renseigné</Typography>
        )}

        {certificatsList.map((cert, i) => (
          <Box key={i} display="flex" gap={2} mt={1}>
            {isEditing ? (
              <>
                <TextField
                  fullWidth
                  label="Titre de la certification"
                  value={cert.title || ''}
                  onChange={(e) => handleCertificatChange(i, 'title', e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Date (ex: 2022 ou Mars 2023)"
                  value={cert.date || ''}
                  onChange={(e) => handleCertificatChange(i, 'date', e.target.value)}
                />
              <IconButton onClick={() => supprimerCertificat(i)} color="error">
                <DeleteIcon />
              </IconButton>
              </>
            ) : (
              <>
                <Typography>{cert.title || 'Non renseigné'}</Typography>
                <Typography>{cert.date || 'Non renseigné'}</Typography>
              </>
            )}
          </Box>
        ))}

        {isEditing && (
          <Box mt={1} mb={2}>
            <button className="btn-outline" onClick={ajouterCertificat}>+ Ajouter un certificat</button>
          </Box>
        )}

      <Divider className="mission-divider" />
    </Paper>
  );
};

export default ProfilRecherche;
